
import React, { useState, useEffect, useCallback } from 'react';
import GenericPopup from '../../genericpopup';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import serviceApi from '../../../services/serviceApi';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface Attachment {
  title: string;
  attachmentUrl: string;
}

interface DiaryItem {
  title: string;
  description: string;
  dateTime: string;
  attachments?: Attachment[];
}
        
interface StudentProp {
  name: string;
  clss: string;
  phone1?: string;
  imageUrl?: string;
  course?: string;
}

function DiaryContent({ student }: { student?: StudentProp }) {
  const [selectedDate, setSelectedDate] = useState<string>('');    //displaying the today s cards
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [subjects, setSubjects] = useState<string[]>([]);
  // const [filteredDates, setFilteredDates] = useState<string[]>([]); // no longer used; backend returns filtered data
  const [rangeQueried, setRangeQueried] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
   
  // Attachment popup state
  const [showAttachmentPopup, setShowAttachmentPopup] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<{ title: string, attachmentUrl: string } | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [homeWorkData, setHomeWorkData] = useState<DiaryItem[]>([]);
  const [classWorkData, setClassWorkData] = useState<DiaryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getEffectiveClass = (): string => {
    if (student?.clss) return student.clss;
    try {
      const s = localStorage.getItem('selectedStudent');
      return s ? (JSON.parse(s)?.clss || 'I-A') : 'I-A';
    } catch {
      return 'I-A';
    }
  };

  const fetchDiaryData = useCallback(async (date: string) => {
    setIsLoading(true);
    try {
      const response = await serviceApi.get<{
        status: string;
        message: string;
        objectData?: {
          diaryList?: Array<{
            id: string;
            title: string;
            date: string;
            clss: string;
            clzz: string;
            course: string;
            homeWork?: string;
            classWork?: string;
            description: string;
            note?: string;
            type?: string;
            allSection: boolean;
          }>;
        };
      }>('qPassApi', {
        serviceName: 'diary',
        clss: getEffectiveClass(),// prefer prop, fallback to localStorage, then default
        date: date,
        entityStatusFlg: false, // example flag
      });

      if (response.status === 'SUCCESS' && response.objectData?.diaryList) {
        // Process the diaryList into homework and classwork
        const diaryItems = response.objectData.diaryList;
        
        // Extract unique subjects from diary items
        const uniqueSubjects = Array.from(new Set(diaryItems.map(item => item.title)))
          .filter(subject => subject); // Remove empty subjects
        setSubjects(uniqueSubjects);
        // Filter for homework items (those with homeWork property or type="HOME-WORK")
        const homeworkItems = diaryItems
          .filter(item => item.homeWork || item.type === 'HOME-WORK')
          .map(item => ({
            title: item.title,
            description: item.homeWork || item.description,
            dateTime: item.date // No default time added
          }));
        
        // Filter for classwork items (those with classWork property)
        const classworkItems = diaryItems
          .filter(item => item.classWork)
          .map(item => ({
            title: item.title,
            description: item.classWork || "",
            dateTime: item.date // No default time added
          }));
        
        // Set the state with the processed data
        setHomeWorkData(homeworkItems);
        setClassWorkData(classworkItems);
      } else {
        // Set empty arrays if API fails or returns no data
        setHomeWorkData([]);
        setClassWorkData([]);
      }
    } catch (err) {
      // Set empty arrays if API call fails
      setHomeWorkData([]);
      setClassWorkData([]);
    } finally {
      setIsLoading(false);
    }
  }, [student]);

  useEffect(() => {
    // Set today's date as default
    const today = new Date().toISOString().split('T')[0];
    setSelectedDate(today);
    
    // Set default values for from and to date fields
    setFromDate(today);
    setToDate(today);
    
    // Fetch today's homework/classwork
    // fetchDiaryData(today);
  }, []);

  useEffect(() => {
  if (selectedDate) {
    fetchDiaryData(selectedDate);
  }
}, [selectedDate, fetchDiaryData]);

  

  const formatToDisplay = (dateStr: string) => {
    if (!dateStr) return "";
    
    const dateParts = dateStr.split(" ");
    const [d, m, y] = dateParts[0].split("/");
    
    // If there's no time component,just return the formatted date
    if (dateParts.length === 1) {
      return `${d}-${m}-${y}`;
    }
    
    // If there's a time component, include it
    const time = dateParts[1] || "";
    const period = dateParts[2] || ""; 
    
    return `${d}-${m}-${y} ${time} ${period}`.trim();
  };

  // const formatToISODate = (dateStr: string) => {
  //   const raw = (dateStr || '').trim();
  //   if (!raw) return '';
  //   const firstPart = raw.split(' ')[0];
  //   const sep = firstPart.includes('/') ? '/' : '-';
  //   const [d, m, y] = firstPart.split(sep);
  //   if (!d || !m || !y) return '';
  //   return `${y}-${(m || '').padStart(2, '0')}-${(d || '').padStart(2, '0')}`;
  // };

  // const formatISOToDisplay = (iso: string) => {
  //   if (!iso) return '';
  //   const [y, m, d] = iso.split('-');
  //   return `${d}-${m}-${y}`;
  // };

  const isPdfFile = (url: string) => {
    return url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('pdf');
  };

  const isImageFile = (url: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    return imageExtensions.some(ext => url.toLowerCase().includes(ext)) ||
      url.toLowerCase().includes('picsum') ||
      url.toLowerCase().includes('placeholder') ||
      url.toLowerCase().includes('image');
  };

  const handleAttachmentClick = (attachment: { title: string, attachmentUrl: string }) => {
    setSelectedAttachment(attachment);
    setShowAttachmentPopup(true);
    setPdfError(null);
    setPageNumber(1);
    setNumPages(null);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    setPdfError(`Failed to load PDF file. Error: ${error.message}. Please check the URL or try again later.`);
  };

  const renderAttachmentPreview = (attachment: { title: string, attachmentUrl: string }) => {
    if (isPdfFile(attachment.attachmentUrl)) {
      return (
        <div
          className="attachment-preview pdf-preview"
          onClick={() => handleAttachmentClick(attachment)}
        >
          <i className="fa-solid fa-file-pdf pdf-icon"></i>
          <span className="attachment-title">{attachment.title}</span>
        </div>
      );
    } else if (isImageFile(attachment.attachmentUrl)) {
      return (
        <div
          className="attachment-preview image-preview"
          onClick={() => handleAttachmentClick(attachment)}
        >
          <img
            src={attachment.attachmentUrl}
            alt={attachment.title}
            className="attachment-thumbnail"
          />
          <span className="attachment-title">{attachment.title}</span>
        </div>
      );
    }
    return null;
  };


  const handleGoClick = async () => {
    if (!fromDate || !toDate) {
      setShowErrorPopup(true);
      return;
    }
    setRangeQueried(true);
    setIsLoading(true);
    // Fetch data for selected range
    try {
      const response = await serviceApi.get<{
        status: string;
        message: string;
        objectData?: {
          diaryList?: Array<{
            id: string;
            title: string;
            date: string;
            clss: string;
            clzz: string;
            course: string;
            homeWork?: string;
            classWork?: string;
            description: string;
            note?: string;
            type?: string;
            allSection: boolean;
          }>;
        };
      }>('qPassApi', {
        serviceName: 'diary',
        clss: getEffectiveClass(),
        date: fromDate,       // start date
        endDate: toDate,      // end date
        entitytype: selectedSubject,
        entityStatusFlg: true,
      });

      if (response.status === 'SUCCESS' && response.objectData?.diaryList) {
        // Process the diaryList into homework and classwork
        const diaryItems = response.objectData.diaryList;
        
        // Extract unique subjects from diary items
        const uniqueSubjects = Array.from(new Set(diaryItems.map(item => item.title)))
          .filter(subject => subject); // Remove empty subjects
        setSubjects(uniqueSubjects);
        
        // Filter for homework items (those with homeWork property or type="HOME-WORK")
        const homeworkItems = diaryItems
          .filter(item => item.homeWork || item.type === 'HOME-WORK')
          .map(item => ({
            title: item.title,
            description: item.homeWork || item.description,
            dateTime: item.date
          }));
        
        // Filter for classwork items (those with classWork property)
        const classworkItems = diaryItems
          .filter(item => item.classWork)
          .map(item => ({
            title: item.title,
            description: item.classWork || "",
            dateTime: item.date
          }));
        
        // Previously applied client-side subject/date filtering and built a date index.
        // Now we trust the backend to filter by date and subject.
        // Set the state with the processed data
        setHomeWorkData(homeworkItems);
        setClassWorkData(classworkItems);
        // setFilteredDates(withinRange);
      } else {
        setHomeWorkData([]);
        setClassWorkData([]);
        // setFilteredDates([]);
      }
    } catch (err) {
      setHomeWorkData([]);
      setClassWorkData([]);
      // setFilteredDates([]);
    } finally {
      setIsLoading(false);
    }

  };


  // const renderFilteredCard = (date: string) => {
    // const hwForDate = homeWorkData.filter(hw =>
    //   formatToISODate(hw.dateTime) === date &&
    //   (!selectedSubject || hw.title.includes(selectedSubject))
    // );
    // const cwForDate = classWorkData.filter(cw =>
    //   formatToISODate(cw.dateTime) === date &&
    //   (!selectedSubject || cw.title.includes(selectedSubject))
    // );

  //   const hwForDate = homeWorkData.filter(hw =>
  //     formatToISODate(hw.dateTime) === date &&
  //     (!selectedSubject || hw.title.includes(selectedSubject))
  //   );
  //   const cwForDate = classWorkData.filter(cw =>
  //     formatToISODate(cw.dateTime) === date &&
  //     (!selectedSubject || cw.title.includes(selectedSubject))
  //   );


  //   return (
  //     <div key={date} className='event-container'>
  //       ...
  //     </div>
  //   );
  // };

  // Render today cards (Homework & Classwork separately)
  const renderTodayCards = () => {
    // const today = selectedDate;
   
    
    // Filter data for today's date only
    // let hwToday = homeWorkData.filter(hw => formatToISODate(hw.dateTime) === today);
    // let cwToday = classWorkData.filter(cw => formatToISODate(cw.dateTime) === today);
    let hwToday = homeWorkData;
    let cwToday = classWorkData;
    
    // Apply subject filter if selected
    // if (selectedSubject) {
    //   hwToday = hwToday.filter(hw => hw.title.includes(selectedSubject));
    //   cwToday = cwToday.filter(cw => cw.title.includes(selectedSubject));
    // }

    return (
      <>
        <div className='event-container'>
          <h3 className='title-text'>Homework</h3>
          <ul className='events-list'>
            {hwToday.length > 0 ? hwToday.map((hw, idx) => (
              <li key={idx} className='event-item'>
                <strong className="subtitle-text">{hw.title}</strong><br />
                <span className='event-description'>{hw.description}</span><br />
                <span className='event-date'>
                  <i className='fa-regular fa-clock colorOrange'></i> {formatToDisplay(hw.dateTime)}
                </span>
                {hw.attachments && hw.attachments.length > 0 && (
                  <div className="attachment-section">
                    <strong className="attachment-section-label">Attachments:</strong>
                    {hw.attachments.map((attachment, attIdx) => (
                      <div key={attIdx}>
                        {renderAttachmentPreview(attachment)}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            )) : <p>No Homework for today</p>}
          </ul>
        </div>

        <div className='event-container'>
          <h3 className='title-text'>Classwork</h3>
          <ul className='events-list'>

            {cwToday.length > 0 ? cwToday.map((cw, idx) => (
              <li key={idx} className='event-item'>
                <strong className="subtitle-text">{cw.title}</strong><br />
                <span className='event-description'>{cw.description}</span><br />
                <span className='event-date'>
                  <i className='fa-solid fa-calendar-day colorOrange'></i> {formatToDisplay(cw.dateTime)}
                </span>
                {cw.attachments && cw.attachments.length > 0 && (
                  <div className="attachment-section">
                    <strong className="attachment-section-label">Attachments:</strong>
                    {cw.attachments.map((attachment, attIdx) => (
                      <div key={attIdx}>
                        {renderAttachmentPreview(attachment)}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            )) : <p>No Classwork for today</p>}
          </ul>
        </div>
      </>
    );
  };

  return (
    <>

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-overlay__content">
            <div className="spinner spinner--lg"></div>
            <span className="loading-text">Loading...</span>
          </div>
        </div>
      )}

      {/* 🔹 Popup always at the top */}
      {showErrorPopup && (
        <GenericPopup
          isOpen={showErrorPopup}
          title="Validation Error"
          message="Please select both From and To dates"
          type="alert"
          onClose={() => setShowErrorPopup(false)}
        />
      )}

      

      

      {/* Attachment Popup */}
      {showAttachmentPopup && selectedAttachment && (
        <div className="popup-overlay">
          <div className="popup-box attachment-popup">
            <div className="popup-header">
              <h3 className="popup-title">{selectedAttachment.title}</h3>
              <button className="close-popup-btn" onClick={() => {
                setShowAttachmentPopup(false);
                setSelectedAttachment(null);
              }}>
                &times;
              </button>
            </div>
            <div className="attachment-popup-content">
              {isPdfFile(selectedAttachment.attachmentUrl) ? (
                <div className="pdf-viewer-container">
                  {pdfError ? (
                    <div className="pdf-error-container">
                      <i className="fa-solid fa-exclamation-triangle pdf-error-icon"></i>
                      <p className="pdf-error-message">{pdfError}</p>
                      <button
                        className="pdf-retry-button"
                        onClick={() => {
                          setPdfError(null);
                          setPageNumber(1);
                        }}
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <div>
                      <Document
                        file={selectedAttachment.attachmentUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={
                          <div className="pdf-loading-container">
                            <i className="fa-solid fa-spinner fa-spin pdf-loading-spinner"></i>
                            <p>Loading PDF...</p>
                          </div>
                        }
                      >
                        <Page pageNumber={pageNumber} width={750} />
                      </Document>

                      {numPages && numPages > 1 && (
                        <div className="pdf-navigation-container">
                          <button
                            className="pdf-nav-button"
                            onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                            disabled={pageNumber <= 1}
                          >
                            Previous
                          </button>
                          <span className="pdf-page-info">
                            Page {pageNumber} of {numPages}
                          </span>
                          <button
                            className="pdf-nav-button"
                            onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                            disabled={pageNumber >= numPages}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : isImageFile(selectedAttachment.attachmentUrl) ? (
                <img
                  src={selectedAttachment.attachmentUrl}
                  alt={selectedAttachment.title}
                  className="attachment-image"
                />
              ) : (
                <p>Unsupported file type</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Date Picker Card */}
      <div className='date-picker-container'>
        <button
          type='button'
          className='date-nav-btn'
          onClick={() => {
            const prevDate = new Date(selectedDate);
            prevDate.setDate(prevDate.getDate() - 1);
        setSelectedDate(prevDate.toISOString().split('T')[0]);
          }}
        >
          <i className="fa-solid fa-chevron-left"></i>
        </button>

        <input
          id='datepicker'
          type='date'
          value={selectedDate}
        onChange={(e) => { setSelectedDate(e.target.value); }}
          className='date-input'
          max={new Date().toISOString().split('T')[0]}
        />

        <button
          type='button'
          className='date-nav-btn'
          onClick={() => {
            const nextDate = new Date(selectedDate);
            nextDate.setDate(nextDate.getDate() + 1);
        setSelectedDate(nextDate.toISOString().split('T')[0]);
          }}
          disabled={selectedDate === new Date().toISOString().split('T')[0]}
        >
          <i className="fa-solid fa-chevron-right"></i>
        </button>
      </div>

      {/* Always show today cards first */}
      {renderTodayCards()}

      {/* Filter Section */}
      <div className="filter-section">
        <label>From:</label>
        <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); /* setFilteredDates([]); */ setRangeQueried(false); }} className="filter-date-input" />
        <label>To:</label>
        <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); /* setFilteredDates([]); */ setRangeQueried(false); }} className="filter-date-input" />
        <label>Subject:</label>
        <select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)} className="filter-subject-dropdown">
          <option value="">All Subjects</option>
          {subjects.length > 0 ? (
            subjects.map((subject, index) => (
              <option key={index} value={subject}>{subject}</option>
            ))
          ) : (
            <>
              <option value="Math">Math</option>
              <option value="Science">Science</option>
              <option value="English">English</option>
              <option value="History">History</option>
            </>
          )}
        </select>
        <button className="go-button" onClick={handleGoClick} disabled={isLoading}>
          {isLoading ? 'Loading...' : 'Go'}
        </button>
      </div>

      {/* Results from service call (no extra frontend filtering) */}
      {rangeQueried && (
        <>
          <div className='event-container'>
            <h3 className='title-text'>Homework</h3>
            <ul className='events-list'>
              {homeWorkData.length > 0 ? homeWorkData.map((hw, idx) => (
                <li key={idx} className='event-item'>
                  <strong className="subtitle-text">{hw.title}</strong><br />
                  <span className='event-description'>{hw.description}</span><br />
                  <span className='event-date'>
                    <i className='fa-regular fa-clock colorOrange'></i> {formatToDisplay(hw.dateTime)}
                  </span>
                </li>
              )) : <p>No Homework in the selected range</p>}
            </ul>
          </div>

          <div className='event-container'>
            <h3 className='title-text'>Classwork</h3>
            <ul className='events-list'>
              {classWorkData.length > 0 ? classWorkData.map((cw, idx) => (
                <li key={idx} className='event-item'>
                  <strong className="subtitle-text">{cw.title}</strong><br />
                  <span className='event-description'>{cw.description}</span><br />
                  <span className='event-date'>
                    <i className='fa-solid fa-calendar-day colorOrange'></i> {formatToDisplay(cw.dateTime)}
                  </span>
                </li>
              )) : <p>No Classwork in the selected range</p>}
            </ul>
          </div>
        </>
      )}
    </>
  );
};

export default DiaryContent;







